import { useEffect, useState } from 'react';
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { Provider as ReduxProvider } from 'react-redux';
// ui
// msal
import { UnauthenticatedTemplate, AuthenticatedTemplate, MsalAuthenticationTemplate, useMsal, MsalProvider, } from "@azure/msal-react";
// app insights
import { AppInsightsErrorBoundary } from "@microsoft/applicationinsights-react-js";
import { reactPlugin } from "./AppInsights";
// custom 
import { routes } from './pages/routes'
import { LoginPage } from './pages/LoginPage';
import { ThemeProvider } from './theme/ThemeProvider'
import { useStyletron, } from 'baseui';
import { SnackbarProvider } from 'baseui/snackbar';
import { ToasterContainer } from 'baseui/toast';
import { useAlarmHubConnection } from './hooks/useAlarmHubConnection';
import { useObjectsHubConnection } from './hooks/useObjectsHubConnection';
import ServiceWorkerWrapper from './components/ServiceWorkerWrapper/ServiceWorkerWrapper';
import { apiConfig } from './config';
import { IntlProvider } from 'react-intl';
import { de } from './lang/de'
import { EN } from './lang/en';
import { ES } from './lang/es';
import { useGetLanguage } from './hooks/useGetLanguage';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { useDeviceRegistration } from './hooks/useDeviceRegistration';
import { DefaultScopes, getAccount, msalConfig } from './msalConfig';
import { MsalError, MsalLoading } from './components/MSAL';
import annotationPlugin from 'chartjs-plugin-annotation';
import zoomPlugin from 'chartjs-plugin-zoom';
import dragdropPlugin from 'chartjs-plugin-dragdata'

import store from './store/store';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Filler,
  Legend,
  TimeSeriesScale,
  CategoryScale
} from 'chart.js';


ChartJS.register(
  CategoryScale,
  TimeSeriesScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  annotationPlugin,
  Title,
  Tooltip,
  Filler,
  Legend,
  zoomPlugin,
  dragdropPlugin
);



const App = ({ msalInstance }) => {
  useEffect(() => {
    if (apiConfig.isDevelopment)
      document.title += " - DEV";
  }, []);

  return (
    <MsalProvider instance={msalInstance}>
      <ReduxProvider store={store}>
        <ThemeProvider>
          <AppUiProviders>
            <ContainerWithAuth />
          </AppUiProviders>
        </ThemeProvider>
      </ReduxProvider>
    </MsalProvider >
  );
}

export default App;

const AppUiProviders = (props) => {
  const { language } = useGetLanguage();
  const [loc, setLoc] = useState<any>();
  const [css, theme] = useStyletron();

  useEffect(() => {
    // see https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes
    if (language === "de" || language.startsWith("de-") || language.startsWith("deu")) {
      setLoc(de);
    }
    else if (language === "es" || language.startsWith("es-")) {
      setLoc(ES);
    }
    else {
      setLoc(EN);
    }
  }, [language]);

  useEffect(() => {
    document.body.style.background = theme.colors.backgroundPrimary;
  }, [theme]);

  return (
    <AppInsightsErrorBoundary onError={() => <h1>I believe something went wrong</h1>} appInsights={reactPlugin}>
      <IntlProvider locale={language} messages={loc}>

        <SnackbarProvider>
          <ToasterContainer>
            <ServiceWorkerWrapper></ServiceWorkerWrapper>
            {props.children}
          </ToasterContainer>
        </SnackbarProvider>

      </IntlProvider>
    </AppInsightsErrorBoundary>
  )
}



function ContainerWithAuth() {
  useDeviceRegistration();

  return (
    <>
      <UnauthenticatedTemplate>
        <LoginPage></LoginPage>
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        <MyAuthentificationWrapper />
      </AuthenticatedTemplate>

    </>
  )
}




const router = createBrowserRouter(routes);

function AuthentificatedContainer() {
  const { connection: alarmHubConnection } = useAlarmHubConnection();
  const { connection: objectsHubConnection } = useObjectsHubConnection();

  return (<RouterProvider router={router}></RouterProvider>)
}

const MyAuthentificationWrapper = () => {
  const { doCreateDeviceReg } = useDeviceRegistration();

  const { instance, inProgress, accounts } = useMsal();
  const [waitLogin, setWaitLogin] = useState<boolean>(true);
  const [error, setError] = useState();

  useEffect(() => {

    if (inProgress === InteractionStatus.None) {
      const accessTokenRequest = {
        scopes: DefaultScopes.scopes,
        account: getAccount(accounts),
      };
      console.info("MyAuthentificationWrapper acquireTokenSilent", accessTokenRequest)

      if (!accessTokenRequest.account) {
        instance.loginRedirect();
      }
      else {
        instance
          .acquireTokenSilent(accessTokenRequest)
          .catch((error) => {
            console.error("MyAuthentificationWrapper acquireTokenSilent ERROR", error)
            if (error instanceof InteractionRequiredAuthError) {

              instance
                .acquireTokenRedirect(accessTokenRequest)
                .then(function (accessTokenResponse) {
                  // Acquire token interactive success
                  //let accessToken = accessTokenResponse.accessToken;
                  // Call your API with token
                  // token is ok 
                  // --> continue to app
                  setWaitLogin(false);
                })
                .catch(function (error) {
                  console.error("MyAuthentificationWrapper acquireTokenSilent 2 ERROR", error)
                  // Acquire token interactive failure
                  console.error(error);
                  setError(error);
                });
            }
            if (error.errorCode !== "monitored_window_timeout" && error.errorCode !== "login_required") // when last login was more than 24h ago this is ok
            {
              console.error("MyAuthentificationWrapper acquireTokenSilent ERROR other", error)
              setError(error);
            }
          })
          .then((accessTokenResponse) => {
            console.info("MyAuthentificationWrapper acquireTokenSilent SUCCESS")
            // Acquire token silent success
            let accessToken = accessTokenResponse?.accessToken;
            // token is ok 
            // --> continue to app
            setWaitLogin(false);
            doCreateDeviceReg();
          });
      }

    }
  }, [instance, accounts, inProgress]);

  return (
    <>
      {error && <MsalError error={error}></MsalError>}
      {waitLogin && <MsalLoading />}
      {!waitLogin && !error && <AuthentificatedContainer></AuthentificatedContainer>}
    </>
  )
}


